import { useMemo } from 'react';
import Column from '@sdk/components/ui/spacing/Column';
import Row from '@sdk/components/ui/spacing/Row';
import Text12 from '@sdk/components/ui/typography/Text12';
import gettext from '@sdk/lang/gettext';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import { ShiftFeedbackRating } from '@sdk/timesheets/types/ShiftFeedback';

import { TooltipContainer } from './lineGraphTooltip.styles';
import { DataPoint } from './lineGraphComponent.hooks';

export type Props = {
  top: number;
  left: number;
  opacity: number;
  dataPoint: DataPoint;
  ratingsFilter: ShiftFeedbackRating[];
};

const ratingToText = {
  [ShiftFeedbackRating.excellent]: gettext('Excellent'),
  [ShiftFeedbackRating.great]: gettext('Great'),
  [ShiftFeedbackRating.good]: gettext('Good'),
  [ShiftFeedbackRating.fair]: gettext('Fair'),
  [ShiftFeedbackRating.poor]: gettext('Poor'),
};

export const LineGraphTooltip = ({ top, left, opacity, dataPoint, ratingsFilter }: Props) => {
  const ratings = useMemo(
    () =>
      [
        ShiftFeedbackRating.poor,
        ShiftFeedbackRating.fair,
        ShiftFeedbackRating.good,
        ShiftFeedbackRating.great,
        ShiftFeedbackRating.excellent,
      ].filter((rating) => !ratingsFilter?.length || ratingsFilter.includes(rating)),
    [ratingsFilter]
  );

  if (!dataPoint) {
    return;
  }

  return (
    <TooltipContainer style={{ top, left, opacity }}>
      <Row>
        <Text12 themeColor={ThemeColorType.gray}>{dataPoint.tooltipCaptionLabel}</Text12>
        &nbsp;
        <Text12 bold themeColor={ThemeColorType.gray}>
          {dataPoint.tooltipCaptionValue}
        </Text12>
      </Row>
      <Row>
        <Text12 themeColor={ThemeColorType.gray}>{gettext('Avg:')}</Text12>&nbsp;
        <Text12 bold themeColor={ThemeColorType.gray}>
          {dataPoint.averageValueRounded}
        </Text12>
      </Row>
      <Row justifyContent="space-between" mTop={10}>
        {ratings.map((rating: ShiftFeedbackRating) => (
          <Column key={rating} alignItems="center">
            <Text12 bold>{dataPoint.tallies[rating]}</Text12>
            <Text12>{ratingToText[rating]}</Text12>
          </Column>
        ))}
      </Row>
    </TooltipContainer>
  );
};
